import React, { useMemo, useRef, useState } from "react";
import nAIShortmark from "../../../assets/nAI-shortmark.webp";
import { showSuccess } from "../../../utils/toastUtils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DetailHeader from "./components/DetailHeader";
import DetailStickyHeader from "./components/DetailStickyHeader";
import InsightShareModal from "./components/InsightShareModal";
import DetailTitle from "./components/DetailTitle";
import InsightBody from "./components/InsightBody";
import EmptyState from "./components/EmptyState";
import { InsightDetailSkeleton } from "./components/InsightsSkeleton";
import useInsightDetail from "./hooks/useInsightDetail";
import useFiData from "./hooks/useFiData";
import { useWatchlistFollow } from "./hooks/useSidebarData";
import useTrackInsightView from "./hooks/useTrackInsightView";
import { ASSISTANT_MODES } from "../../index.jsx";
import NebulaAssistantSidebar from "../../assistant/NebulaAssistantSidebar";
import { useInsightContext } from "../../chat-context/presets";
import { usePageTitle, usePageSuggestions } from "../../chat-context/hooks";

function deriveCtaSuggestions(detail) {
  const items = detail?.structured?.cta?.cta_items;
  if (!Array.isArray(items) || !items.length) return null;
  const mapped = items
    .map((it) => {
      const label = typeof it?.label === "string" ? it.label.trim() : "";
      if (!label) return null;
      const description =
        typeof it?.description === "string" ? it.description.trim() : "";
      const prompt = description ? `${label} â€” ${description}` : label;
      return { label, prompt };
    })
    .filter(Boolean);
  return mapped.length ? mapped : null;
}

function deriveInsightTitle(detail, fallback) {
  if (!detail) return fallback;
  if (detail.title) return detail.title;
  if (detail.name) return detail.name;
  const summary = detail.summary || "";
  const firstLine = summary.split("\n").find((s) => s.trim().length > 0) || "";
  const clean = firstLine.replace(/^#+\s*/, "").trim();
  if (clean) return clean;
  if (detail.symbol || detail.category) {
    return `${detail.symbol || ""}${detail.category ? ` Â· ${detail.category}` : ""}`.trim();
  }
  return fallback;
}

export default function InsightDetailPage() {
  const { insightId: analysisId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const decodedId = analysisId ? decodeURIComponent(analysisId) : null;
  const { detail, loading, error } = useInsightDetail(decodedId);
  useTrackInsightView(!loading && !error ? detail : null);

  const tickers = useMemo(
    () => (detail?.symbol ? [detail.symbol] : []),
    [detail?.symbol],
  );
  const { data: marketData } = useFiData(tickers);
  const market = detail?.symbol
    ? marketData?.[detail.symbol.toUpperCase()]
    : null;

  const insightTitle = useMemo(
    () => deriveInsightTitle(detail, decodedId),
    [detail, decodedId],
  );
  const hasResolvedTitle =
    !!detail && !!insightTitle && insightTitle !== decodedId;
  useInsightContext(
    decodedId,
    hasResolvedTitle ? insightTitle : null,
    location.pathname,
  );
  usePageTitle(hasResolvedTitle ? insightTitle : null);

  const ctaSuggestions = useMemo(() => deriveCtaSuggestions(detail), [detail]);
  const suggestionsReady = !loading && (!!detail || !!error);
  usePageSuggestions(suggestionsReady ? ctaSuggestions || [] : null);

  const { isFollowed, toggle: handleFollow } = useWatchlistFollow(detail?.symbol);

  const onBack = () => navigate("/insights");

  const scrollRef = useRef(null);
  const printRef = useRef(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const handleScroll = (e) => {
    const scrolled = e.target.scrollTop > 100;
    if (scrolled !== showStickyHeader) setShowStickyHeader(scrolled);
  };

  const handleShare = () => setShareModalOpen(true);

  const handlePrint = () => {
    if (typeof window === 'undefined' || !printRef.current) return;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try { return Array.from(sheet.cssRules).map((r) => r.cssText).join('\n'); }
        catch { return sheet.href ? `@import url('${sheet.href}');` : ''; }
      }).join('\n');

    const sym = detail?.symbol || '';
    const price = market?.priceClose ?? detail?.structured?.price ?? null;
    const changePct = market?.pctChange ?? market?.pricePctChgIntraDay ?? detail?.structured?.change_pct ?? null;
    const printDate = new Date().toLocaleDateString('vi-VN', { year: 'numeric', month: 'long', day: 'numeric' });

    let priceColor = '#636369';
    if (typeof changePct === 'number' && changePct > 0) priceColor = '#16A34A';
    if (typeof changePct === 'number' && changePct < 0) priceColor = '#DC2626';
    const priceHtml = (price !== null || changePct !== null)
      ? `<span style="font-size:13px;font-weight:600;color:${priceColor};">${price !== null ? price.toLocaleString('vi-VN') : ''}${changePct !== null ? ' ' + (changePct > 0 ? '+' : '') + changePct.toFixed(2) + '%' : ''}</span>`
      : '';

    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>${sym ? sym + ' · ' : ''}Báo cáo phân tích · Simplize</title>
      <style>
        ${styles}
        @page{margin:0;}
        body{margin:0;padding:0 32px;color:#111827;}
        [data-no-print]{display:none!important;}
        table{width:100%;border-collapse:collapse;}
        thead{display:table-header-group;}
        tfoot{display:table-footer-group;}
        tbody{display:table-row-group;}
        .ph{display:flex;align-items:center;gap:12px;padding:16px 0 14px;border-bottom:1px solid #ECECED;margin-bottom:24px;}
        .ph-symbol{font-size:16px;font-weight:700;color:#111827;}
        .ph-date{font-size:12px;color:#9CA3AF;margin-left:auto;}
        .pf{padding:12px 0 16px;border-top:1px solid #ECECED;font-size:11px;color:#9CA3AF;text-align:center;}
      </style>
    </head><body>
      <table>
        <thead><tr><td>
          <div class="ph">
            <img src="${nAIShortmark}" style="height:20px;width:auto;" alt="Nebula AI">
            ${sym ? `<span class="ph-symbol">${sym}</span>` : ''}
            ${priceHtml}
            <span class="ph-date">${printDate}</span>
          </div>
        </td></tr></thead>
        <tfoot><tr><td>
          <div class="pf">Báo cáo được tạo tự động bởi Nebula AI · Không phải tư vấn đầu tư · Chỉ mang tính tham khảo</div>
        </td></tr></tfoot>
        <tbody><tr><td>${printRef.current.innerHTML}</td></tr></tbody>
      </table>
    </body></html>`);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <>
    <div className="flex flex-col h-full bg-[#F8FDFC] text-[#111827] pt-[60px] md:pt-0">
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
          <DetailStickyHeader
            scrollRef={scrollRef}
            isVisible={showStickyHeader}
            detail={detail}
            market={market}
            onBack={onBack}
            onFollow={handleFollow}
            isFollowed={isFollowed}
            onPrint={handlePrint}
            onShare={handleShare}
          />
          <main
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto overflow-x-hidden px-[16px] pt-[24px] pb-[32px] md:px-[32px] md:pt-[24px] md:pb-[40px] min-w-0"
          >
            <DetailHeader detail={detail} market={market} onBack={onBack} onFollow={handleFollow} isFollowed={isFollowed} onPrint={handlePrint} onShare={handleShare} className="mb-[16px]" />

            <div className="max-w-[733px] mx-auto flex flex-col gap-[16px]">
              {loading && !detail && <InsightDetailSkeleton />}
              {error && (
                <div className="py-[20px] text-[#DC2626] text-[13px]">
                  {error}
                </div>
              )}
              {!loading && !error && !detail && (
                <EmptyState
                  title="Không tìm thấy báo cáo"
                  description="Báo cáo có thể đã bị gỡ hoặc ID không hợp lệ. Hãy quay lại danh sách phân tích để chọn mã khác."
                />
              )}

              {detail && (
                <div ref={printRef} className="contents">
                  <DetailTitle detail={detail} market={market} />
                  <div className="text-[12px] text-[#636369] text-center mb-[8px]">
                    Báo cáo được tạo tự động bởi Nebula AI · Không phải tư vấn
                    đầu tư · Chỉ mang tính tham khảo
                  </div>
                  <InsightBody detail={detail} />

                  <div className="text-[12px] text-[#636369] italic">
                    <span className="text-[#111827]">Miễn trừ trách nhiệm</span>: Nội dung trên được tạo bởi Nebula AI
                    (Simplize) dựa trên dữ liệu tài chính và mô hình định giá
                    tại thời điểm truy xuất. Là sản phẩm của hệ thống tự động,
                    kết quả có thể chứa sai sót hoặc không phản ánh đầy đủ mọi
                    yếu tố ảnh hưởng đến giá trị chứng khoán. Simplize không đảm
                    bảo tính chính xác, đầy đủ, hoặc kịp thời của thông tin được
                    trình bày. Nội dung mang tính chất tham khảo, không phải
                    khuyến nghị đầu tư hay lời mời mua bán chứng khoán, và không
                    thay thế tư vấn đầu tư chuyên nghiệp theo quy định của Luật
                    Chứng khoán 2019. Mọi khoản đầu tư đều có rủi ro, bao gồm
                    rủi ro mất vốn gốc. Kết quả quá khứ không đảm bảo hiệu suất
                    tương lai. Nhà đầu tư nên đối chiếu nhiều nguồn và tham khảo
                    chuyên gia tài chính được cấp phép trước khi quyết định.
                    Simplize và Nebula AI miễn trừ mọi trách nhiệm đối với tổn
                    thất phát sinh từ việc sử dụng thông tin này.
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        <NebulaAssistantSidebar
          defaultTab="chat"
          chatAssistantConfig={{
            cacheKey: detail?.id.toString(),
          }}
        />
      </div>
    </div>

    <InsightShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} />
    </>
  );
}
